import { createContext, useContext, useMemo, useState } from 'react'
import type { TablesInsert } from '@/types/database'
import type { TaskWithRelations } from '@/types/domain'
import { TaskFormDialog } from './task-form-dialog'
import { TaskDetailSheet } from './task-detail-sheet'

type FormState =
  | { mode: 'create'; defaults?: Partial<TablesInsert<'tasks'>> }
  | { mode: 'edit'; task: TaskWithRelations }
  | null

interface TaskActions {
  openCreate: (defaults?: Partial<TablesInsert<'tasks'>>) => void
  openEdit: (task: TaskWithRelations) => void
  openDetail: (taskId: string) => void
  closeDetail: () => void
}

const Ctx = createContext<TaskActions | null>(null)

export function TaskActionsProvider({ children }: { children: React.ReactNode }) {
  const [form, setForm] = useState<FormState>(null)
  const [detailId, setDetailId] = useState<string | null>(null)

  const actions = useMemo<TaskActions>(
    () => ({
      openCreate: (defaults) => setForm({ mode: 'create', defaults }),
      openEdit: (task) => setForm({ mode: 'edit', task }),
      openDetail: (taskId) => setDetailId(taskId),
      closeDetail: () => setDetailId(null),
    }),
    [],
  )

  return (
    <Ctx.Provider value={actions}>
      {children}
      <TaskFormDialog state={form} onClose={() => setForm(null)} />
      <TaskDetailSheet
        taskId={detailId}
        onClose={() => setDetailId(null)}
        onEdit={(t) => setForm({ mode: 'edit', task: t })}
      />
    </Ctx.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTaskActions() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useTaskActions deve estar dentro de <TaskActionsProvider>')
  return ctx
}
